import {
    faBitcoinSign,
    faBolt,
    faChartLine,
    faChartPie,
    faCircleDollarToSlot,
    faCircleNodes,
    faCircleQuestion,
    faClockRotateLeft,
    faDesktop,
    faFileInvoiceDollar,
    faFileLines,
    faGear,
    faLifeRing,
    faPenRuler,
    faPenToSquare,
    faPuzzlePiece,
    faRobot,
    faRocket,
    faSignOutAlt,
    faTimes,
    faUserGear,
    faWallet,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import { clearSession, doLogout } from '../../services/AuthService';
import SideBarGroup from './SideBarGroup';
import SideBarItem, { ActivePathsProvider } from './SideBarItem';

// Every destination rendered below; SideBarItem uses it to pick the best match
// for the current path (so nested screens keep their section selected).
const NAV_PATHS = [
    '/dashboard',
    '/reports',
    '/wallet',
    '/orders',
    '/strategies',
    '/automations',
    '/automations/backtests',
    '/orderTemplates',
    '/webhooks',
    '/withdrawTemplates',
    '/monitors',
    '/onboarding',
    '/faq',
    '/settings',
    '/symbols',
    '/',
];

function SideBar() {
    const navigate = useNavigate();

    function cleanAndRedirect() {
        clearSession();
        navigate('/');
    }

    function onLogoutClick(event) {
        doLogout()
            .then(response => cleanAndRedirect())
            .catch(error => {
                console.error(error);
                cleanAndRedirect();
            });
    }

    return (
        <nav
            id="sidebarMenu"
            className="sidebar d-lg-block bg-gray-800 text-white collapse"
            datasimplebar="true"
        >
            <div className="sidebar-inner px-4 pt-3">
                <div className="user-card d-flex d-md-none align-items-center justify-content-between justify-content-md-center pb-4">
                    <div className="collapse-close d-md-none">
                        <a
                            href="#sidebarMenu"
                            data-bs-toggle="collapse"
                            data-bs-target="#sidebarMenu"
                            aria-controls="sidebarMenu"
                            aria-expanded="true"
                            aria-label="Toggle navigation"
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="icon icon-xs my-vb-icon"
                            />
                        </a>
                    </div>
                </div>
                <ActivePathsProvider paths={NAV_PATHS}>
                    <ul className="nav flex-column pt-3 pt-md-0">
                        <li className="nav-item">
                            <Link
                                to="/dashboard"
                                className="nav-link d-flex align-items-center"
                            >
                                <span className="sidebar-icon">
                                    <img
                                        src="/img/favicon/favicon-32x32.png"
                                        height="32"
                                        width="32"
                                        alt="Argos TraderBot Logo"
                                    />
                                </span>
                                <span className="mt-1 ms-1 sidebar-text">
                                    Argos TraderBot
                                </span>
                            </Link>
                        </li>
                        <li
                            role="separator"
                            className="dropdown-divider mt-4 mb-3 border-gray-700"
                        ></li>
                        <SideBarGroup
                            id="submenu-intelligence"
                            text="Intelligence"
                            paths={['/dashboard', '/reports', '/wallet']}
                            icon={
                                <FontAwesomeIcon
                                    icon={faChartPie}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            }
                        >
                            <SideBarItem to="/dashboard" text="Dashboard">
                                <FontAwesomeIcon
                                    icon={faChartLine}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem to="/reports" text="Reports">
                                <FontAwesomeIcon
                                    icon={faFileLines}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem to="/wallet" text="Wallet">
                                <FontAwesomeIcon
                                    icon={faWallet}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        </SideBarGroup>
                        <SideBarItem
                            to="/orders"
                            text={
                                localStorage.getItem('hasFutures') === 'true'
                                    ? 'Orders & Positions'
                                    : 'Orders'
                            }
                        >
                            <FontAwesomeIcon
                                icon={faFileInvoiceDollar}
                                className="icon icon-xs me-2 my-vb-icon"
                            />
                        </SideBarItem>
                        {localStorage.getItem('hasStrategies') === 'true' && (
                            <SideBarItem to="/strategies" text="Strategies">
                                <FontAwesomeIcon
                                    icon={faPuzzlePiece}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        )}
                        <SideBarGroup
                            id="submenu-automations"
                            text="Bots"
                            paths={['/automations', '/automations/backtests']}
                            icon={
                                <FontAwesomeIcon
                                    icon={faRobot}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            }
                        >
                            <SideBarItem to="/automations" text="Automations">
                                <FontAwesomeIcon
                                    icon={faBolt}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem
                                to="/automations/backtests"
                                text="Backtests"
                            >
                                <FontAwesomeIcon
                                    icon={faClockRotateLeft}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        </SideBarGroup>
                        <SideBarGroup
                            id="submenu-templates"
                            text="Action Templates"
                            paths={[
                                '/orderTemplates',
                                '/webhooks',
                                '/withdrawTemplates',
                            ]}
                            icon={
                                <FontAwesomeIcon
                                    icon={faPenRuler}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            }
                        >
                            <SideBarItem to="/orderTemplates" text="Order">
                                <FontAwesomeIcon
                                    icon={faPenToSquare}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem to="/webhooks" text="WebHook">
                                <FontAwesomeIcon
                                    icon={faCircleNodes}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem
                                to="/withdrawTemplates"
                                text="Withdraw"
                            >
                                <FontAwesomeIcon
                                    icon={faCircleDollarToSlot}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        </SideBarGroup>
                        <SideBarItem to="/monitors" text="Monitors">
                            <FontAwesomeIcon
                                icon={faDesktop}
                                className="icon icon-xs me-2 my-vb-icon"
                            />
                        </SideBarItem>
                        <SideBarGroup
                            id="submenu-help"
                            text="Help"
                            paths={['/onboarding', '/faq']}
                            icon={
                                <FontAwesomeIcon
                                    icon={faLifeRing}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            }
                        >
                            <SideBarItem
                                to="/onboarding"
                                text="Getting Started"
                            >
                                <FontAwesomeIcon
                                    icon={faRocket}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem to="/faq" text="FAQ">
                                <FontAwesomeIcon
                                    icon={faCircleQuestion}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        </SideBarGroup>
                        <SideBarGroup
                            id="submenu-settings"
                            text="Settings"
                            paths={['/settings', '/symbols']}
                            icon={
                                <FontAwesomeIcon
                                    icon={faGear}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            }
                        >
                            <SideBarItem to="/settings" text="Personal">
                                <FontAwesomeIcon
                                    icon={faUserGear}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                            <SideBarItem to="/symbols" text="Symbols">
                                <FontAwesomeIcon
                                    icon={faBitcoinSign}
                                    className="icon icon-xs me-2 my-vb-icon"
                                />
                            </SideBarItem>
                        </SideBarGroup>
                        <li
                            role="separator"
                            className="dropdown-divider mt-4 mb-3 border-gray-700"
                        ></li>
                        <SideBarItem
                            to="/"
                            text="Logout"
                            onClick={onLogoutClick}
                        >
                            <FontAwesomeIcon
                                icon={faSignOutAlt}
                                className="icon icon-xs me-2 my-vb-icon"
                            />
                        </SideBarItem>
                    </ul>
                </ActivePathsProvider>
            </div>
        </nav>
    );
}

export default SideBar;
